import React, { useMemo, useRef, useState } from 'react';
import { Alert, Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { AttendanceComments } from '../../components/AttendanceComments';
import { AppCard } from '../../components/AppCard';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AttendanceStatusBadge } from '../../components/AttendanceStatusBadge';
import {
  AttendancePunchRecordModal,
  type AttendanceRecordDetail,
} from '../../components/AttendancePunchRecordModal';
import {
  CheckoutBreakModal,
  type CheckoutBreakValues,
} from '../../components/CheckoutBreakModal';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { WorkHoursRing } from '../../components/WorkHoursRing';
import { REPORT_ACCESS_PERMISSIONS } from '../../config/reportCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useDashboard } from '../../hooks/useDashboard';
import {
  loadTrackingSettings,
  useAttendanceLocationTracking,
} from '../../hooks/useLocationTracking';
import {
  captureTrackingFix,
  getLocationTracker,
  type TrackerStatus,
} from '../../services/locationTracker';
import {
  defaultSelfServicePunchSource,
  markAttendanceBreakIn,
  markAttendanceBreakOut,
  getWfhCheckInBlockMessage,
  markAttendanceCheckIn,
  markAttendanceCheckOut,
  recordBreakTimesForCheckout,
  type AttendanceActionResult,
} from '../../services/attendanceService';
import { getTodayAttendance } from '../../services/dashboardService';
import type { TodayAttendance } from '../../types/dashboard';
import { getOrCreateDeviceIdentifier } from '../../services/kioskTokenStore';
import { withSingleFaceCapture } from '../../services/faceVerificationService';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  beginAttendancePunch,
  endAttendancePunch,
} from '../../utils/attendancePunchGuard';
import {
  recoverPunchFromToday,
  type PunchKind,
} from '../../utils/attendancePunchRecovery';
import {
  formatWorkedMinutes,
  getTodayAttendanceStatusLabel,
} from '../../utils/attendanceStatus';
import {
  currentBusinessClock,
  resolveCheckoutBreakPrompt,
  timestampToClock,
} from '../../utils/checkoutBreak';
import { canViewTeamDashboard } from '../../utils/approvalRoles';
import { readPunchCoordinates } from '../../utils/punchLocation';
import { isTrackedAttendanceSource } from '../../utils/locationTracking';
import {
  MOBILE_WORK_LOCATIONS,
  resolveAttendanceLocationMode,
  resolveSelfServicePunchSource,
  type MobileWorkLocationCode,
} from '../../utils/attendanceLocationMode';
import { formatTime, todayBusinessCalendarDate } from '../../utils/dateTime';
import { isWebPlatform } from '../../utils/webLayout';
import {
  checkedOutTotalWorkMinutes,
  formatTotalWorkHours,
  formatPolicyHours,
  isAttendanceCheckedOut,
  resolveTotalHoursCoverage,
} from '../../utils/totalHours';

type Props = BottomTabScreenProps<MainTabParamList, 'Attendance'>;

type PunchNotice = {
  tone: 'success' | 'error';
  title: string;
  message: string;
  details: AttendanceRecordDetail[];
};

const PUNCH_SUCCESS = {
  in: ['Checked in', 'Your check-in was recorded.'],
  out: ['Checked out', 'Your check-out was recorded.'],
  breakOut: ['Break started', 'Your break start was recorded.'],
  breakIn: ['Break ended', 'Your return to work was recorded.'],
} as const;

const PUNCH_FAILURE = {
  in: 'Check-in',
  out: 'Check-out',
  breakOut: 'Start break',
  breakIn: 'End break',
} as const;

function recordDetail(
  label: string,
  value: string | null | undefined,
): AttendanceRecordDetail | null {
  const text = value?.trim();
  if (!text || text === 'Not available') return null;
  return { label, value: text };
}

function pushDetail(
  details: AttendanceRecordDetail[],
  label: string,
  value: string | null | undefined,
) {
  const detail = recordDetail(label, value);
  if (detail) details.push(detail);
}

function webSuccessNotice(
  kind: PunchKind,
  result: AttendanceActionResult | null | undefined,
  today: TodayAttendance | null,
  employeeName: string | null | undefined,
  employeeNumber: string | null | undefined,
  checkoutWithBreak = false,
): PunchNotice {
  const details: AttendanceRecordDetail[] = [];
  pushDetail(details, 'Employee', employeeName);
  pushDetail(details, 'Employee number', employeeNumber);
  if (kind === 'in') {
    pushDetail(
      details,
      'Check-in',
      formatTime(
        today?.checkInTime ??
          result?.checkInTime ??
          result?.localEventTime ??
          result?.eventTime,
      ),
    );
  } else if (kind === 'out') {
    pushDetail(
      details,
      'Check-out',
      formatTime(today?.checkOutTime ?? result?.localEventTime ?? result?.eventTime),
    );
  } else if (kind === 'breakOut') {
    pushDetail(
      details,
      'Break start',
      formatTime(
        result?.localEventTime ?? result?.eventTime ?? today?.openBreakStartedAt,
      ),
    );
  } else {
    pushDetail(
      details,
      'Break end',
      formatTime(result?.localEventTime ?? result?.eventTime),
    );
    const breakMinutes = today?.breakMinutes ?? result?.breakMinutes;
    pushDetail(
      details,
      'Total break',
      breakMinutes == null ? null : formatWorkedMinutes(breakMinutes),
    );
  }
  if (today) {
    pushDetail(details, 'Status', getTodayAttendanceStatusLabel(today));
    pushDetail(details, 'Date', today.attendanceDate);
  }
  const titles = {
    in: 'Check-In Record',
    out: 'Check-Out Record',
    breakOut: 'Break Record',
    breakIn: 'Break Record',
  } as const;
  const message =
    kind === 'out' && checkoutWithBreak
      ? 'Your break and check-out were recorded.'
      : PUNCH_SUCCESS[kind][1];
  return { tone: 'success', title: titles[kind], message, details };
}

type PunchCoordinates = Awaited<ReturnType<typeof readPunchCoordinates>>;

/**
 * Tracked client visits need a real GPS fix for the start/end location, so
 * they use the device location service; other modes keep the existing reader.
 */
async function readTrackedPunchCoordinates(
  tracked: boolean,
  freshFixMs: number,
): Promise<PunchCoordinates> {
  if (!tracked) return readPunchCoordinates();
  const recent = getLocationTracker().latestFix();
  const fix =
    recent && Date.now() - recent.timestamp <= freshFixMs
      ? recent
      : await captureTrackingFix(10000, 15000);
  if (!fix) return readPunchCoordinates();
  return {
    latitude: fix.latitude,
    longitude: fix.longitude,
    gpsAccuracyMeters: fix.accuracyMeters,
  };
}

function trackingStatusText(status: TrackerStatus, attendanceId: number): string | null {
  if (status.attendanceId !== attendanceId) return null;
  switch (status.state) {
    case 'tracking':
      return status.offline
        ? 'Live tracking on · offline, saving locally'
        : 'Live tracking on';
    case 'starting':
      return 'Starting live tracking…';
    case 'permission_denied':
    case 'permission_blocked':
      return 'Location permission needed for tracking';
    case 'location_disabled':
      return 'Turn on location services for tracking';
    case 'error':
      return status.error ?? 'Live tracking stopped';
    default:
      return null;
  }
}

/** States the employee can recover from by asking again. */
function canRetryTracking(status: TrackerStatus, attendanceId: number): boolean {
  return (
    status.attendanceId === attendanceId &&
    (status.state === 'error' || status.state === 'permission_denied')
  );
}

function formatShiftTime(value: string | null | undefined): string {
  if (!value) return 'Not available';
  return value.replace(/\b([01]?\d|2[0-3])([:.])(\d{2})\b/g, (_, hour, separator, minute) => {
    const twelveHour = Number(hour) % 12 || 12;
    return `${String(twelveHour).padStart(2, '0')}${separator}${minute}`;
  });
}

export function AttendanceHomeScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const dashboard = useDashboard(auth.permissions, auth.user?.userId);
  const attendance = dashboard.data?.attendance;
  const root = navigation.getParent<NativeStackNavigationProp<RootStackParamList>>();
  const statusLabel = getTodayAttendanceStatusLabel(attendance);
  const statusNotes = attendance?.dayStatus?.notes ?? [];
  const [punchBusy, setPunchBusy] = useState(false);
  const [selectedWorkLocation, setSelectedWorkLocation] =
    useState<MobileWorkLocationCode | null>(null);
  const [wfhBlockMessage, setWfhBlockMessage] = useState<string | null>(null);
  const wfhNoticeRequest = useRef(0);
  const [breakPrompt, setBreakPrompt] = useState<'missing' | 'open' | null>(
    null,
  );
  const [notice, setNotice] = useState<PunchNotice | null>(null);
  const canMark = auth.hasPermission(PERMISSIONS.ATTENDANCE_MARK);
  const showCheckOut = Boolean(
    attendance?.canCheckOut || attendance?.isOnBreak || attendance?.canEndBreak,
  );
  const locationMode = resolveAttendanceLocationMode({
    attendanceMode: attendance?.attendanceMode,
  });
  // Total Work Hours / Not Covered only after a valid check-out.
  const isCheckedOut = attendance ? isAttendanceCheckedOut(attendance) : false;
  const totalWorkMinutes = attendance
    ? checkedOutTotalWorkMinutes(attendance)
    : null;
  const tracking = useAttendanceLocationTracking(attendance, {
    enabled: canMark,
    userId: auth.user?.userId != null ? String(auth.user.userId) : null,
  });
  const trackingStatus =
    attendance?.attendanceId && tracking.tracked
      ? trackingStatusText(tracking.status, attendance.attendanceId)
      : null;

  const themed = useMemo(
    () => ({
      sectionTitle: { ...typography.sectionTitle, color: palette.textPrimary },
      help: { ...typography.body, color: palette.textSecondary },
      detailLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      detail: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
      locationTitle: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
      locationDescription: { ...typography.caption, color: palette.textSecondary },
    }),
    [palette],
  );

  async function selectWorkLocation(code: MobileWorkLocationCode) {
    setSelectedWorkLocation(code);
    if (code !== 'WFH_APP') {
      wfhNoticeRequest.current += 1;
      setWfhBlockMessage(null);
      return;
    }
    const requestId = wfhNoticeRequest.current + 1;
    wfhNoticeRequest.current = requestId;
    const message = await getWfhCheckInBlockMessage();
    if (wfhNoticeRequest.current !== requestId) return;
    setWfhBlockMessage(message);
  }

  function showFailure(title: string, message: string) {
    if (!isWebPlatform()) {
      Alert.alert(title, message);
      return;
    }
    setNotice({ tone: 'error', title, message, details: [] });
  }

  async function showSuccess(
    kind: PunchKind,
    result: AttendanceActionResult | null | undefined,
    checkoutWithBreak = false,
  ) {
    const today = await getTodayAttendance().catch(() => null);
    if (!isWebPlatform()) {
      const title =
        kind === 'out' && checkoutWithBreak
          ? 'Checked out'
          : PUNCH_SUCCESS[kind][0];
      let message =
        kind === 'out' && checkoutWithBreak
          ? 'Your break and check-out were recorded.'
          : PUNCH_SUCCESS[kind][1];
      if (kind === 'breakIn') {
        const breakMinutes = today?.breakMinutes ?? result?.breakMinutes;
        const duration =
          breakMinutes == null ? null : formatWorkedMinutes(breakMinutes);
        const endTime = formatTime(
          result?.localEventTime ?? result?.eventTime,
        );
        const lines = [message];
        if (endTime && endTime !== 'Not available') {
          lines.push(`Break end: ${endTime}`);
        }
        if (duration && duration !== 'Not available') {
          lines.push(`Total break: ${duration}`);
        }
        message = lines.join('\n');
      } else if (kind === 'breakOut') {
        const startTime = formatTime(
          result?.localEventTime ??
            result?.eventTime ??
            today?.openBreakStartedAt,
        );
        if (startTime && startTime !== 'Not available') {
          message = `${message}\nBreak start: ${startTime}`;
        }
      }
      Alert.alert(title, message);
      return;
    }
    setNotice(
      webSuccessNotice(
        kind,
        result,
        today,
        auth.user?.displayName,
        auth.user?.employeeNumber,
        checkoutWithBreak,
      ),
    );
  }

  /** Tracking ends the moment the check-out is saved, before the screen refreshes. */
  async function stopTrackingAfterCheckout() {
    const tracker = getLocationTracker();
    if (attendance?.attendanceId && tracker.getStatus().attendanceId === attendance.attendanceId) {
      await tracker.stop().catch(() => undefined);
    }
  }

  async function isTrackedPunch(kind: PunchKind): Promise<boolean> {
    if (kind !== 'in' && kind !== 'out') return false;
    const source = kind === 'in' ? selectedWorkLocation : attendance?.attendanceMode;
    return isTrackedAttendanceSource(source, await loadTrackingSettings());
  }

  async function resolvePunchFailure(
    kind: PunchKind,
    caught: unknown,
  ): Promise<AttendanceActionResult | null> {
    const today = await getTodayAttendance().catch(() => null);
    const recovered = recoverPunchFromToday(kind, today, caught);
    if (recovered) {
      await dashboard.refreshDashboard();
      return recovered;
    }
    // Always refresh after a failed punch so buttons match server state.
    try {
      await Promise.resolve(dashboard.refreshDashboard());
    } catch {
      // Keep the original punch error for the UI.
    }
    return null;
  }

  async function punch(kind: PunchKind) {
    if (punchBusy || !beginAttendancePunch()) return;
    if (kind === 'in' && !selectedWorkLocation) {
      endAttendancePunch();
      showFailure(
        'Select work location',
        'Choose where you are working before checking in.',
      );
      return;
    }
    if (kind === 'in' && selectedWorkLocation === 'WFH_APP') {
      const message = wfhBlockMessage ?? (await getWfhCheckInBlockMessage());
      if (message) {
        endAttendancePunch();
        setWfhBlockMessage(message);
        showFailure('Check-in failed', message);
        return;
      }
    }
    if (kind === 'out' && attendance) {
      const prompt = resolveCheckoutBreakPrompt(attendance);
      if (prompt.kind !== 'none') {
        endAttendancePunch();
        setBreakPrompt(prompt.kind);
        return;
      }
    }
    setNotice(null);
    setPunchBusy(true);
    try {
      const trackedPunch = await isTrackedPunch(kind);
      const settings = trackedPunch ? await loadTrackingSettings() : null;
      const coordinates =
        kind === 'in' || kind === 'out'
          ? await readTrackedPunchCoordinates(
              trackedPunch,
              (settings?.sampleIntervalSeconds ?? 0) * 2000,
            )
          : null;
      const location = coordinates
        ? {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            gpsAccuracyMeters: coordinates.gpsAccuracyMeters,
          }
        : undefined;
      const attendanceSource =
        kind === 'in'
          ? selectedWorkLocation!
          : resolveSelfServicePunchSource(
              attendance?.attendanceMode,
              defaultSelfServicePunchSource(),
            );
      const deviceIdentifier = await getOrCreateDeviceIdentifier();
      const punchOptions = { ...location, attendanceSource, deviceIdentifier };
      const result =
        kind === 'in'
          ? await markAttendanceCheckIn(punchOptions)
          : kind === 'out'
            ? await markAttendanceCheckOut(punchOptions)
            : kind === 'breakOut'
              ? await markAttendanceBreakOut(punchOptions)
              : await markAttendanceBreakIn(punchOptions);
      if (kind === 'out') await stopTrackingAfterCheckout();
      await dashboard.refreshDashboard();
      await showSuccess(kind, result);
    } catch (caught) {
      const recovered = await resolvePunchFailure(kind, caught);
      if (recovered) {
        if (kind === 'out') await stopTrackingAfterCheckout();
        await showSuccess(kind, recovered);
      } else {
        showFailure(
          `${PUNCH_FAILURE[kind]} failed`,
          attendanceErrorMessage(caught, 'Unable to mark attendance.'),
        );
      }
    } finally {
      setPunchBusy(false);
      endAttendancePunch();
    }
  }

  async function submitBreakAndCheckout(values: CheckoutBreakValues) {
    if (punchBusy || !beginAttendancePunch()) return;
    setPunchBusy(true);
    try {
      const attendanceSource = resolveSelfServicePunchSource(
        attendance?.attendanceMode,
        defaultSelfServicePunchSource(),
      );
      const deviceIdentifier = await getOrCreateDeviceIdentifier();
      const trackedPunch = await isTrackedPunch('out');
      const settings = trackedPunch ? await loadTrackingSettings() : null;
      const coordinates = await readTrackedPunchCoordinates(
        trackedPunch,
        (settings?.sampleIntervalSeconds ?? 0) * 2000,
      );
      // One in-app face scan covers the break entries and the check-out.
      const result = await withSingleFaceCapture(async () => {
        if (values.recordBreak && !values.recordBreakStart) {
          await recordBreakTimesForCheckout({
            attendanceDate:
              attendance?.attendanceDate ?? todayBusinessCalendarDate(),
            startClock: values.startClock,
            endClock: values.endClock,
            recordBreakStart: false,
            attendanceSource,
            deviceIdentifier,
          });
        }
        const breakTimes =
          values.recordBreak && values.recordBreakStart
            ? {
                breakStartTime: values.startClock,
                breakEndTime: values.endClock,
              }
            : {};
        return markAttendanceCheckOut(
          coordinates
            ? {
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
                gpsAccuracyMeters: coordinates.gpsAccuracyMeters,
                attendanceSource,
                deviceIdentifier,
                ...breakTimes,
              }
            : { attendanceSource, deviceIdentifier, ...breakTimes },
        );
      });
      setBreakPrompt(null);
      await stopTrackingAfterCheckout();
      await dashboard.refreshDashboard();
      await showSuccess('out', result, values.recordBreak);
    } catch (caught) {
      const recovered = await resolvePunchFailure('out', caught);
      if (recovered) {
        setBreakPrompt(null);
        await stopTrackingAfterCheckout();
        await showSuccess('out', recovered, values.recordBreak);
      } else {
        showFailure(
          'Check-out failed',
          attendanceErrorMessage(caught, 'Unable to mark attendance.'),
        );
      }
    } finally {
      setPunchBusy(false);
      endAttendancePunch();
    }
  }

  if (dashboard.isLoading && !dashboard.data) {
    return (
      <AppScreen contentStyle={styles.center}>
        <AppLoader message="Loading attendance" />
      </AppScreen>
    );
  }
  if (dashboard.error) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ErrorState message={dashboard.error} onRetry={dashboard.loadDashboard} />
      </AppScreen>
    );
  }

  return (
    <AppScreen
      scroll
      refreshControl={
        <RefreshControl
          refreshing={dashboard.isRefreshing || punchBusy}
          onRefresh={dashboard.refreshDashboard}
          tintColor={palette.primary}
        />
      }
    >
      <ScreenHeader
        title="Attendance"
      />

      <View style={styles.content}>
        <AppCard style={styles.card}>
          <AttendanceStatusBadge label={statusLabel} />
          {statusNotes.length > 0 ? (
            <Text style={themed.detailLabel}>{statusNotes.join('\n')}</Text>
          ) : null}
          <View style={styles.statusLayout}>
            <View style={styles.grid}>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Check-in</Text>
                <Text style={themed.detail}>{formatTime(attendance?.checkInTime)}</Text>
                {tracking.tracked && attendance?.attendanceId && attendance.checkInTime ? (
                  <>
                    {trackingStatus ? (
                      <Text style={themed.locationDescription}>{trackingStatus}</Text>
                    ) : null}
                    {canRetryTracking(tracking.status, attendance.attendanceId) ? (
                      <AppButton
                        title="Retry tracking"
                        variant="secondary"
                        onPress={() => void getLocationTracker().retry()}
                      />
                    ) : null}
                    <AppButton
                      title="Tracking Details"
                      variant="secondary"
                      onPress={() =>
                        root?.navigate(routes.locationTrackingDetails, {
                          attendanceId: attendance.attendanceId!,
                        })
                      }
                    />
                  </>
                ) : null}
              </View>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Check-out</Text>
                <Text style={themed.detail}>{formatTime(attendance?.checkOutTime)}</Text>
              </View>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Total Break</Text>
                <Text style={themed.detail}>
                  {formatWorkedMinutes(attendance?.breakMinutes ?? null)}
                </Text>
              </View>
              {isWebPlatform() && attendance?.openBreakStartedAt ? (
                <View style={styles.detailBlock}>
                  <Text style={themed.detailLabel}>Break start</Text>
                  <Text style={themed.detail}>
                    {formatTime(attendance.openBreakStartedAt)}
                  </Text>
                </View>
              ) : null}
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Worked</Text>
                <Text style={themed.detail}>
                  {formatWorkedMinutes(attendance?.workedMinutes ?? null)}
                </Text>
              </View>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Expected Work Hours</Text>
                <Text style={themed.detail}>
                  {formatPolicyHours(attendance?.expectedWorkMinutes, 'Not available')}
                </Text>
              </View>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Total Work Hours</Text>
                <Text
                  style={[
                    themed.detail,
                    resolveTotalHoursCoverage(
                      totalWorkMinutes,
                      attendance?.spreadMinutes,
                    ) === 'not_covered'
                      ? { color: palette.error }
                      : null,
                  ]}
                >
                  {formatTotalWorkHours(
                    totalWorkMinutes,
                    attendance?.checkInTime && !isCheckedOut
                      ? 'After check-out'
                      : 'Not available',
                  )}
                </Text>
              </View>
              <View style={styles.detailBlock}>
                <Text style={themed.detailLabel}>Shift</Text>
                <Text style={themed.detail}>
                  {formatShiftTime(attendance?.shiftName)}
                </Text>
              </View>
              {locationMode ? (
                <View style={styles.detailBlock}>
                  <Text style={themed.detailLabel}>Attendance type</Text>
                  <Text style={themed.detail}>{locationMode}</Text>
                </View>
              ) : null}
            </View>
            <WorkHoursRing
              workedMinutes={attendance?.workedMinutes ?? null}
              expectedMinutes={attendance?.expectedWorkMinutes ?? null}
              showCoverage={isCheckedOut}
            />
          </View>
        </AppCard>

        {canMark ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>Mark attendance</Text>
            <Text style={themed.help}>
              Office attendance is recorded at the office kiosk. For mobile
              check-in, choose where you are working today.
            </Text>
            {attendance?.canCheckIn ? (
              <>
                <View accessibilityRole="radiogroup" accessibilityLabel="Work location" style={styles.locationOptions}>
                  {MOBILE_WORK_LOCATIONS.map(option => {
                    const selected = selectedWorkLocation === option.code;
                    return (
                      <Pressable
                        key={option.code}
                        accessibilityRole="radio"
                        accessibilityLabel={option.label}
                        accessibilityState={{ checked: selected, disabled: punchBusy }}
                        disabled={punchBusy}
                        onPress={() => selectWorkLocation(option.code)}
                        style={[
                          styles.locationOption,
                          { borderColor: selected ? palette.primary : palette.border },
                          selected && { backgroundColor: `${palette.primary}12` },
                        ]}
                      >
                        <View style={[styles.radio, { borderColor: selected ? palette.primary : palette.textMuted }]}>
                          {selected ? <View style={[styles.radioDot, { backgroundColor: palette.primary }]} /> : null}
                        </View>
                        <View style={styles.locationCopy}>
                          <Text style={themed.locationTitle}>{option.label}</Text>
                          <Text style={themed.locationDescription}>{option.description}</Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
                {selectedWorkLocation === 'WFH_APP' && wfhBlockMessage ? (
                  <Text style={[themed.help, { color: palette.error }]}>
                    {wfhBlockMessage}
                  </Text>
                ) : null}
                <AppButton
                  title={punchBusy ? 'Checking in…' : 'Check in'}
                  onPress={() => punch('in')}
                  disabled={
                    punchBusy ||
                    !selectedWorkLocation ||
                    (selectedWorkLocation === 'WFH_APP' && Boolean(wfhBlockMessage))
                  }
                />
              </>
            ) : null}
            {attendance?.attendanceId && attendance?.checkInTime ? (
              <AttendanceComments key={attendance.attendanceId}
                attendanceId={attendance.attendanceId} canMark={canMark} disabled={punchBusy}
                refreshKey={`${attendance.currentStatus}:${attendance.checkOutTime}`} />
            ) : null}
            {attendance?.canStartBreak ? (
              <AppButton
                title={punchBusy ? 'Starting break…' : 'Start break'}
                onPress={() => punch('breakOut')}
                disabled={punchBusy}
              />
            ) : null}
            {showCheckOut ? (
              <AppButton
                title={punchBusy ? 'Checking out…' : 'Check out'}
                onPress={() => punch('out')}
                disabled={punchBusy}
                variant={
                  attendance?.canStartBreak || attendance?.canEndBreak
                    ? 'secondary'
                    : 'primary'
                }
              />
            ) : null}
            {attendance?.canEndBreak ? (
              <AppButton
                title={punchBusy ? 'Ending break…' : 'End break'}
                onPress={() => punch('breakIn')}
                disabled={punchBusy}
              />
            ) : null}
            {!attendance?.canCheckIn &&
            !showCheckOut &&
            !attendance?.canStartBreak &&
            !attendance?.canEndBreak ? (
              <Text style={themed.help}>
                No attendance action is available right now.
              </Text>
            ) : null}
          </AppCard>
        ) : null}

        {auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW) ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>History</Text>
            <Text style={themed.help}>
              Browse past attendance days, durations, and events.
            </Text>
            <AppButton
              title="View Attendance History"
              onPress={() => root?.navigate(routes.attendanceHistory)}
            />
            <AppButton
              title="My Weekly Attendance"
              variant="secondary"
              onPress={() => root?.navigate(routes.myWeeklyAttendance)}
            />
            {auth.hasPermission(PERMISSIONS.ATTENDANCE_CORRECT) &&
            !auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) ? (
              <AppButton
                title="Request attendance correction"
                variant="secondary"
                onPress={() =>
                  root?.navigate(routes.attendanceCorrectionHome)
                }
              />
            ) : null}
          </AppCard>
        ) : null}

        {auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) &&
        auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW) ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>Admin dashboard</Text>
            <Text style={themed.help}>
              Organization attendance snapshot — present, absent, leave, WFH,
              late, short hours, and missing checkout.
            </Text>
            <AppButton
              title="Open Admin Dashboard"
              onPress={() => navigation.navigate(routes.adminDashboard)}
            />
          </AppCard>
        ) : null}

        {auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) &&
        auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]) ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>Team attendance</Text>
            <Text style={themed.help}>
              Admin view of all employees for today or any selected date —
              present, absent, check-in/out, and work hours.
            </Text>
            <AppButton
              title="Employee Attendance"
              onPress={() => navigation.navigate(routes.attendanceReport)}
            />
          </AppCard>
        ) : null}

        {canViewTeamDashboard(auth.roles, auth.permissions) ? (
          <AppCard style={styles.card}>
            <Text style={themed.sectionTitle}>Team attendance</Text>
            <Text style={themed.help}>
              Your team members for today or any selected date — present,
              absent, check-in/out, and work hours.
            </Text>
            <AppButton
              title="Team Attendance"
              onPress={() => root?.navigate(routes.teamAttendanceReport)}
            />
          </AppCard>
        ) : null}
      </View>
      <CheckoutBreakModal
        visible={breakPrompt !== null}
        mode={breakPrompt ?? 'missing'}
        checkInClock={timestampToClock(attendance?.checkInTime)}
        checkOutClock={
          timestampToClock(attendance?.checkOutTime) ?? currentBusinessClock()
        }
        initialStart={attendance?.openBreakStartedAt}
        busy={punchBusy}
        onCancel={() => {
          if (!punchBusy) setBreakPrompt(null);
        }}
        onConfirm={submitBreakAndCheckout}
      />
      {isWebPlatform() ? (
        <AttendancePunchRecordModal
          visible={notice !== null}
          title={notice?.title ?? ''}
          message={notice?.message ?? ''}
          details={notice?.details ?? []}
          tone={notice?.tone ?? 'success'}
          onClose={() => setNotice(null)}
        />
      ) : null}
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center' },
  content: { marginTop: spacing.lg },
  card: { gap: spacing.md, marginBottom: spacing.md },
  statusLayout: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  grid: {
    flex: 1,
    minWidth: 160,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  detailBlock: { minWidth: '44%', gap: spacing.xs },
  locationOptions: { gap: spacing.sm },
  locationOption: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  radio: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: { width: 10, height: 10, borderRadius: 5 },
  locationCopy: { flex: 1, gap: spacing.xs },
});
